import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import { formatDuration, formatHms, formatLocalTime } from '../utils/suiteFormatters';
import iconProject from '../assets/report-project.svg';
import iconGroup from '../assets/report-group.svg';
import iconScripts from '../assets/report-scripts.svg';
import iconDuration from '../assets/report-duration.svg';
import iconEnded from '../assets/report-ended.svg';
import iconDownload from '../assets/report-download.svg';
import useProjectDisplayName from '../hooks/useProjectDisplayName';
import ReportDownloadButton from '../../../shared/components/ReportDownloadButton';
import '../styles/ExecutionStudio.css';

/**
 * Excluded, cancelled and never-dispatched members count as neither passed nor
 * failed (BR-39). `NO_RESULT` is the Core's word for a member that ended
 * without producing permutations — it is not a failure, so it is not shown as
 * one.
 */
const OUT_OF_SCOPE_STATUSES = ['OUT_OF_SCOPE', 'CANCELLED', 'NO_RESULT', 'NOT_DISPATCHED'];

/**
 * Cancelled and excluded both read as "out of scope". A member that was
 * dispatched and produced nothing is a different thing, so it says so rather
 * than being folded into the same word.
 */
const OUT_OF_SCOPE_LABELS = {
  NO_RESULT: 'NO RESULT',
  NOT_DISPATCHED: 'NOT DISPATCHED',
};

const isOutOfScope = (status) => OUT_OF_SCOPE_STATUSES.includes(status);

/** The five fact tiles, each with the Figma icon for its slot. */
const FACT_ICONS = {
  PROJECT: iconProject,
  GROUP: iconGroup,
  SCRIPTS: iconScripts,
  DURATION: iconDuration,
  ENDED: iconEnded,
};

/**
 * Consolidated report for a finished batch (Figma `11-run-window-with-details`,
 * `Main - ConsolidatedReportWidget`).
 *
 * **Every figure here is rendered, not derived.** `passRate`, the permutation
 * counts and the three-way script rollup all come straight off
 * `consolidated-summary.json` — recomputing them risks a different rounding
 * from the stored report and the API.
 *
 * The download is the Core's own stored report for this run, not a document
 * built in the browser — so what is downloaded is what the Core wrote.
 *
 * Per TONIC-PEX-IMPL-FE-001 Task 7.
 */
const ConsolidatedReport = ({ report, projectId, loading, onOpenMember, onDownloadReport, reportFormats, downloading, showMembers }) => {
  const endedId = useId();
  // The summary carries the original folder name — show the alias if renamed.
  // The run's own projectId comes first: it is the one we submitted with.
  const projectDisplayName = useProjectDisplayName(projectId || report?.projectId, report?.projectName);

  if (loading) {
    return (
      <section className="tsg-report">
        <p className="tsg-empty-inline"><Loader2 size={16} className="tsg-spin" /> Loading consolidated report...</p>
      </section>
    );
  }

  if (!report) return null;

  const {
    batchId, groupingName, overallStatus, passRate,
    passedPermutations, executedPermutations, failedPermutations,
    scriptsPassed, scriptsPartial, scriptsFailed, scriptsOutOfScope,
    runDateTime, overallDurationMs,
    members = [],
  } = report;

  // The bar mirrors passRate rather than recomputing a ratio of its own
  const barWidth = typeof passRate === 'number' ? Math.max(0, Math.min(100, passRate)) : 0;
  // Out-of-scope scripts are only mentioned when there are some — a trailing
  // ", 0 out of scope" on every report is noise.
  const scriptsSubline = `${scriptsPassed} scripts fully passed, ${scriptsPartial} partial, `
    + `${scriptsFailed} fully failed`
    + (scriptsOutOfScope ? `, ${scriptsOutOfScope} out of scope` : '');
  const endedAt = runDateTime && overallDurationMs != null
    ? new Date(new Date(runDateTime).getTime() + overallDurationMs).toISOString()
    : null;

  const facts = [
    { key: 'PROJECT', value: projectDisplayName || '—', title: projectDisplayName },
    { key: 'GROUP', value: groupingName || '—', title: groupingName },
    { key: 'SCRIPTS', value: members.length },
    { key: 'DURATION', value: formatHms(overallDurationMs), hint: overallDurationMs != null ? '(elapsed)' : null },
    // The viewer's own system time, converted from the UTC instant (BR-42)
    { key: 'ENDED', value: endedAt ? formatLocalTime(endedAt) : '—' },
  ];

  return (
    <section className="tsg-report" aria-label="Consolidated report">
      <header className="tsg-report__head">
        <div className="tsg-report__title-wrap">
          <span className="tsg-report__glyph" aria-hidden="true"><span /><span /><span /></span>
          <h3 className="tsg-report__title">Consolidated Report</h3>
          <span className={`tsg-status-pill tsg-status-pill--${String(overallStatus).toLowerCase()}`}>
            {overallStatus}
          </span>
        </div>
        {batchId && (
          <span className="tsg-report__run">Run <strong>{String(batchId).slice(0, 8)}</strong></span>
        )}
      </header>

      <dl className="tsg-report__facts">
        {facts.map(({ key, value, hint, title }) => (
          <div key={key} className="tsg-report__fact">
            <span className="tsg-report__fact-icon" aria-hidden="true">
              <span className="tsg-report__fact-disc">
                <img src={FACT_ICONS[key]} width="17" height="17" alt="" />
              </span>
            </span>
            <div className="tsg-report__fact-text">
              <dt id={key === 'ENDED' ? endedId : undefined}>{key}</dt>
              <dd aria-labelledby={key === 'ENDED' ? endedId : undefined} title={title || undefined}>
                {value}
              </dd>
              {/* On its own line under the value, so a narrow tile never cuts it off */}
              {hint && <span className="tsg-report__fact-hint">{hint}</span>}
            </div>
          </div>
        ))}
      </dl>

      <div className="tsg-report__bar">
        <span className="tsg-report__bar-fill" style={{ width: `${barWidth}%` }} />
      </div>

      <div className="tsg-report__foot">
        {/* All three come straight from the payload — see §6a's field map */}
        <p className="tsg-report__lines">
          <span className="tsg-report__headline">
            {passedPermutations} of {executedPermutations} permutations passed ({passRate}%)
          </span>
          {failedPermutations != null && (
            <span className="tsg-report__subline">{failedPermutations} failed</span>
          )}
          <span className="tsg-report__subline">{scriptsSubline}</span>
        </p>
        {onDownloadReport && (
          // A dropdown to pick HTML or PDF when the run has both
          <ReportDownloadButton
            className="tsg-report__download"
            formats={reportFormats}
            onDownload={onDownloadReport}
            downloading={downloading}
            icon={<img src={iconDownload} width="14" height="14" alt="" />}
          />
        )}
      </div>

      {showMembers && (
        <div className="tsg-report__rows">
          {members.map((member, index) => {
            const scoped = isOutOfScope(member.status);
            return (
              <button
                key={member.runId || `${member.scriptName}-${index}`}
                className={`tsg-report__row${scoped ? ' tsg-report__row--scoped' : ''}`}
                onClick={() => member.runId && onOpenMember?.(member)}
                disabled={!member.runId || !onOpenMember}
                // A drill-down is a pointer to the existing run results (AC-17)
                title={!onOpenMember
                  ? member.scriptName
                  : member.runId ? `Open ${member.scriptName} results` : 'This script did not run'}
              >
                <span className="tsg-report__row-name">{member.scriptName}</span>
                <span className="tsg-row-tags">
                  {(member.scriptTypes || []).map((t) => (
                    <span key={t} className="tsg-chip tsg-chip--muted tsg-chip--tiny">{String(t).toUpperCase()}</span>
                  ))}
                </span>
                <span className={`tsg-status-pill tsg-status-pill--${String(member.status).toLowerCase()}`}>
                  {scoped ? (OUT_OF_SCOPE_LABELS[member.status] || 'OUT OF SCOPE') : member.status}
                </span>
                <span className="tsg-report__row-perms">
                  {member.permutations ? `${member.permutations.passed}/${member.permutations.executed}` : '—'}
                </span>
                <span className="tsg-report__row-time">{formatLocalTime(member.startDateTime)}</span>
                <span className="tsg-report__row-time">{formatLocalTime(member.endDateTime)}</span>
                <span className="tsg-report__row-time">{formatDuration(member.durationMs)}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

ConsolidatedReport.propTypes = {
  /** consolidated-summary.json, as written by the Core (contract §6.3). */
  report: PropTypes.shape({
    manifestVersion: PropTypes.number,
    batchId: PropTypes.string,
    groupingId: PropTypes.string,
    groupingName: PropTypes.string,
    projectId: PropTypes.string,
    projectName: PropTypes.string,
    overallStatus: PropTypes.string,
    passRate: PropTypes.number,
    runDateTime: PropTypes.string,
    timeZone: PropTypes.string,
    overallDurationMs: PropTypes.number,
    // Permutation totals are flat on the report, not nested under a
    // `permutations` object the way a member row carries them.
    totalPermutations: PropTypes.number,
    executedPermutations: PropTypes.number,
    passedPermutations: PropTypes.number,
    failedPermutations: PropTypes.number,
    scriptsPassed: PropTypes.number,
    scriptsPartial: PropTypes.number,
    scriptsFailed: PropTypes.number,
    scriptsOutOfScope: PropTypes.number,
    members: PropTypes.arrayOf(PropTypes.shape({
      runId: PropTypes.string,
      runnableId: PropTypes.string,
      scriptName: PropTypes.string,
      scriptTypes: PropTypes.arrayOf(PropTypes.string),
      status: PropTypes.string,
      permutations: PropTypes.shape({
        total: PropTypes.number,
        executed: PropTypes.number,
        passed: PropTypes.number,
        failed: PropTypes.number,
      }),
      startDateTime: PropTypes.string,
      endDateTime: PropTypes.string,
      durationMs: PropTypes.number,
      failureReason: PropTypes.string,
    })),
  }),
  /** The run's project, for when the summary itself does not carry one. */
  projectId: PropTypes.string,
  loading: PropTypes.bool,
  onOpenMember: PropTypes.func,
  /** Downloads the Core's stored report. Absent when there is none to fetch. */
  onDownloadReport: PropTypes.func,
  /** Formats the run has — ['html'], or ['html', 'pdf'] for the dropdown. */
  reportFormats: PropTypes.arrayOf(PropTypes.string),
  downloading: PropTypes.bool,
  /**
   * The per-member table. The live run window turns it off — its own script
   * rows sit directly below and already show every member.
   */
  showMembers: PropTypes.bool,
};

ConsolidatedReport.defaultProps = {
  report: null,
  projectId: null,
  loading: false,
  /** Absent where there is nowhere to drill down to, e.g. the history view. */
  onOpenMember: null,
  onDownloadReport: null,
  reportFormats: ['html'],
  downloading: false,
  showMembers: true,
};

export default ConsolidatedReport;
